import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Chip, Typography } from "@mui/material";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { useTranslation } from "react-i18next";
import { useCRUD } from "../../../../api/crud";
import { EQUIPMENT_ATTRIBUTES_ENDPOINT } from "../../../../consts";
import { useBreadcrumbs } from "../../../../components/ui/breadcrumps/breadcrumbsContext";
import {
  DetailHeader,
  DetailPage,
  DetailRow,
  DetailSection,
} from "../../../../components/ui/detailView";

interface AttributeChoice {
  id: number;
  name: string;
}

interface EquipmentAttribute {
  id: number;
  name: string;
  choices?: AttributeChoice[];
}

export const ConfigurationBasicInfo = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(EQUIPMENT_ATTRIBUTES_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [attribute, setAttribute] = useState<EquipmentAttribute | null>(null);

  useEffect(() => {
    const fetchConfiguration = async () => {
      if (!id) return;
      const response = await get(id);
      if (!response?.data) return;
      setAttribute(response.data);
      setOverride(
        `/equipment-section/configurations/${id}`,
        response.data.name || id
      );
    };

    const handleUpdated = () => fetchConfiguration();
    window.addEventListener("equipmentAttributeUpdated", handleUpdated);
    fetchConfiguration();
    return () => {
      window.removeEventListener("equipmentAttributeUpdated", handleUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!attribute) return <DetailPage loading />;

  const choices = Array.isArray(attribute.choices) ? attribute.choices : [];

  return (
    <DetailPage>
      <DetailHeader icon={<TuneOutlinedIcon />} title={attribute.name} />

      <DetailSection title={t("Basic Information")}>
        <DetailRow label={t("Name")} value={attribute.name} />
      </DetailSection>

      <DetailSection title={t("Choices")} padded>
        {choices.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {choices.map((choice) => (
              <Chip key={choice.id} label={choice.name} size="small" variant="outlined" />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {t("No choices")}
          </Typography>
        )}
      </DetailSection>
    </DetailPage>
  );
};
